import { useInfiniteQuery } from '@tanstack/react-query';
import { endpoints, getIDFromUrl, withSideload } from '@rossum/api-client';
import { api } from '../../../lib/apiClient';
import { resolveNextPageParam } from '../../../lib/resolveNextPageParam';
import { queueInfoKeys } from '../keys';

export const useInfiniteQueueStatsForEngine = (engineId: number) => {
  const infiniteQuery = useInfiniteQuery({
    queryKey: queueInfoKeys.stats(`${engineId}`),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.request(
        withSideload(
          endpoints.engines.queueStats(engineId, {
            page: pageParam,
            pageSize: 50,
          }),
          { queues: true }
        )
      );

      const queues = response.queues.filter(queue =>
        response.results.some(result => result.url === queue.url)
      );

      const workspacesIds = queues.flatMap(queue =>
        queue.workspace ? [getIDFromUrl(queue.workspace)] : []
      );

      const workspaces = await api.request(
        endpoints.workspaces.list({ id: workspacesIds })
      );

      const data = queues.flatMap(queue => {
        // when a user deletes a queue, it is scheduled to be deleted. Queues that are scheduled for deletion should not be shown in the UI
        if (queue.deleteAfter !== null) return [];

        const workspace = workspaces.results.find(
          workspace => workspace.url === queue.workspace
        );

        const stats = response.results.find(item => item.url === queue.url);

        return [
          {
            queue,
            workspace,
            stats,
          },
        ];
      });

      return {
        data,
        pagination: response.pagination,
      };
    },
    getNextPageParam: resolveNextPageParam,
  });

  const data = {
    results: infiniteQuery.data?.pages.flatMap(page => page.data) ?? [],
    totalCount: infiniteQuery.data?.pages[0]?.pagination.total ?? 0,
  };

  return {
    ...infiniteQuery,
    data,
  };
};
